import { forwardRef, memo, useId, type ReactElement, type SVGProps } from 'react';

/**
 * Иконки Азиаточки: сетка 24×24, линия 1.5, скруглённые концы и углы.
 *
 * Геометрия описана слоями, а рисуется через маску, поэтому из одного описания
 * получаются и контурный, и сплошной вариант, а пересечения линий не темнеют
 * при полупрозрачном цвете. Цвет — currentColor корневого svg (как у Phosphor),
 * поэтому классы text-* и CSS color работают без изменений.
 */

type ShapeTag = 'path' | 'circle' | 'rect';

export interface ShapeSpec {
  tag: ShapeTag;
  attrs: Record<string, string | number>;
  /** Точка: отрезок нулевой длины, который рисуется чуть толще линии. */
  dot?: boolean;
  /** В сплошном варианте вырезается целиком, а не только по контуру. */
  hole?: boolean;
}

/** Строка — это атрибут d у path. */
export type Shape = string | ShapeSpec;

export interface Glyph {
  /** Тело: в сплошном варианте заливается. */
  body?: Shape[];
  /** Детали поверх тела: в сплошном варианте вырезаются. */
  detail?: Shape[];
  /** Открытые линии: всегда линии. */
  line?: Shape[];
  /** Элементы поверх остальных (колёса машины и т. п.): под ними стирается зазор. */
  over?: Shape[];
  /** Детали элементов из over: в сплошном варианте вырезаются. */
  overDetail?: Shape[];
}

export const circle = /*#__NO_SIDE_EFFECTS__*/ (cx: number, cy: number, r: number): ShapeSpec => ({
  tag: 'circle',
  attrs: { cx, cy, r },
});

export const rect = /*#__NO_SIDE_EFFECTS__*/ (
  x: number,
  y: number,
  width: number,
  height: number,
  rx: number
): ShapeSpec => ({ tag: 'rect', attrs: { x, y, width, height, rx } });

export const path = /*#__NO_SIDE_EFFECTS__*/ (d: string, transform?: string): ShapeSpec => ({
  tag: 'path',
  attrs: transform ? { d, transform } : { d },
});

export const dot = /*#__NO_SIDE_EFFECTS__*/ (x: number, y: number): ShapeSpec => ({
  ...path(`M${x} ${y}h.01`),
  dot: true,
});

export const hole = /*#__NO_SIDE_EFFECTS__*/ (shape: Shape): ShapeSpec => ({
  ...(typeof shape === 'string' ? path(shape) : shape),
  hole: true,
});

export type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'color' | 'strokeWidth'> {
  /** Ширина и высота. По умолчанию 1em — как у Phosphor. */
  size?: number | string;
  color?: string;
  /** Совместимость с Phosphor: fill и duotone → сплошной вариант, bold → линия 2. */
  weight?: IconWeight;
  /** Толщина линии поверх той, что задаёт weight. */
  strokeWidth?: number;
  mirrored?: boolean;
  /** Подпись для скринридера. Без неё иконка считается декоративной. */
  alt?: string;
}

const STROKE_BY_WEIGHT: Record<IconWeight, number> = {
  thin: 1,
  light: 1.25,
  regular: 1.5,
  bold: 2,
  fill: 1.5,
  duotone: 1.5,
};

const WHITE = '#fff';
const BLACK = '#000';
const toSpec = (shape: Shape) => (typeof shape === 'string' ? path(shape) : shape);

function renderLayers(glyph: Glyph, solid: boolean, stroke: number) {
  const cut = solid ? BLACK : WHITE;
  const layer = (shapes: Shape[] | undefined, fill: string, color: string, width?: number) =>
    shapes?.length ? (
      <g fill={fill} stroke={color} strokeWidth={width}>
        {shapes.map((shape, index) => {
          const { tag: Tag, attrs, dot: isDot, hole: isHole } = toSpec(shape);
          return (
            <Tag
              key={index}
              {...attrs}
              strokeWidth={isDot ? stroke + 0.5 : undefined}
              fill={isHole && solid ? BLACK : undefined}
            />
          );
        })}
      </g>
    ) : null;

  return (
    <>
      {layer(glyph.body, solid ? WHITE : 'none', WHITE)}
      {layer(glyph.detail, 'none', cut)}
      {layer(glyph.line, 'none', WHITE)}
      {layer(glyph.over, BLACK, BLACK, stroke + 3)}
      {layer(glyph.over, solid ? WHITE : BLACK, WHITE)}
      {layer(glyph.overDetail, 'none', cut)}
    </>
  );
}

const toPascalCase = (name: string) =>
  name.replace(/(^|-)([a-z0-9])/g, (_, __, char: string) => char.toUpperCase());

export function createIcon(name: string, glyph: Glyph) {
  const hasBody = Boolean(glyph.body?.length || glyph.over?.length);
  const layered = [glyph.body, glyph.detail, glyph.line, glyph.over, glyph.overDetail].flatMap(
    (shapes) => shapes ?? []
  );
  // Одна фигура без деталей рисуется линией напрямую: маска ей не нужна.
  // Сплошное тело всё равно идёт через маску — иначе заливка и обводка одного
  // элемента наложатся и полупрозрачный цвет потемнеет по краю.
  const single = layered.length === 1 ? toSpec(layered[0]) : null;
  const masks = new Map<string, ReactElement>();

  const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
    { size = '1em', color, weight, strokeWidth, mirrored, alt, children, ...rest },
    ref
  ) {
    const maskId = `azia-icon-${useId().replace(/[^\w-]/g, '')}`;
    const solid = weight === 'fill' || weight === 'duotone';
    const baseStroke = strokeWidth ?? STROKE_BY_WEIGHT[weight ?? 'regular'];
    // У иконок из одних линий в сплошном варианте нечего заливать — делаем их чуть плотнее.
    const stroke = solid && !hasBody && strokeWidth === undefined ? baseStroke * 1.25 : baseStroke;
    const labelled = Boolean(alt || rest['aria-label'] || rest['aria-labelledby']);
    const paint = color ?? 'currentColor';

    let content: ReactElement;
    if (single && !(solid && hasBody)) {
      const { tag: Tag, attrs, dot: isDot } = single;
      content = (
        <Tag
          {...attrs}
          fill="none"
          stroke={paint}
          strokeWidth={isDot ? stroke + 0.5 : stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    } else {
      const key = `${solid}|${stroke}`;
      let layers = masks.get(key);
      if (!layers) {
        layers = <>{renderLayers(glyph, solid, stroke)}</>;
        masks.set(key, layers);
      }
      content = (
        <>
          <mask id={maskId} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
            {layers}
          </mask>
          <rect width="24" height="24" mask={`url(#${maskId})`} stroke="none" />
        </>
      );
    }

    return (
      <svg
        ref={ref}
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill={paint}
        transform={mirrored ? 'scale(-1, 1)' : undefined}
        aria-hidden={labelled ? undefined : true}
        role={labelled ? 'img' : undefined}
        data-azia-icon={name}
        {...rest}
      >
        {alt ? <title>{alt}</title> : null}
        {content}
        {children}
      </svg>
    );
  });

  Icon.displayName = `${toPascalCase(name)}Icon`;
  const MemoIcon = memo(Icon);
  MemoIcon.displayName = Icon.displayName;
  return MemoIcon;
}

export type IconComponent = ReturnType<typeof createIcon>;
